import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDate } from "../../utils/datesAdapter";
import "../../assets/fonts/NotoSans-Regular-normal.js";
import "../../assets/fonts/NotoSans-Bold-normal.js";
import "../../assets/fonts/NotoSansSymbols2-Regular-normal.js";
import { PumpRecommendations } from "../../hooks/useFormPoolCalculations";
import { FirstStepFormData } from "../../schemas/validationSchemas";
import { calculateSurfaceArea } from "../../utils/calculateSurface";
import { transformFormDataToPoolInfo } from "./utils/dataTransformers";
import { getCustomerTableData } from "../../components/ResultStep/utils/getCustomerTableData";
import { getPoolInfoTableData } from "../../components/ResultStep/utils/getPoolInfoTableData";
import { getOperatingPeriodTableData } from "../../components/ResultStep/utils/gerOprationPeriodTableData";
import { addDisclaimer } from "../../components/ResultStep/utils/getDisclaimer";
import { getPoolImage } from "../../components/ResultStep/utils/getPoolImage";
import { createRecommendationImages } from "../../components/ResultStep/utils/createRecommendationImages";
import { salesforceService } from "../salesforce";
import DaveyLogo2 from "../../assets/davey-logo-02.png";
import { addPdfRecommendationBanner } from "../../components/ResultStep/utils/getPdfBanner";
import html2canvas from "html2canvas";

interface PDFMeasures {
  surfaceAreaTop: number;
  estimateVol: number;
}

export interface PDFGenerationOptions {
  download?: boolean;
  isEmpty?: boolean;
  graphRef?: React.RefObject<HTMLElement>;
  formData: FirstStepFormData;
  recommendations?: PumpRecommendations | null;
  user: string;
  email: string;
  isExistingPool: boolean;
  latestRecommendationReportId?: string;
  isMobile?: boolean;
}

export class PDFGenerator {
  private readonly DEFAULT_OPTIONS = {
    download: true,
    isEmpty: false,
  };

  async generatePDF(options: PDFGenerationOptions): Promise<void> {
    const config = { ...this.DEFAULT_OPTIONS, ...options };
    try {
      if (config.isEmpty || !config.recommendations) {
        await this.generateEmptyResultPDF(config);
      } else {
        await this.generateFullRecommendationPDF(config);
      }
    } catch (error) {
      console.error("PDF generation failed:", error);
      throw new Error("Failed to generate PDF. Please try again.");
    }
  }

  private async generateEmptyResultPDF(
    options: PDFGenerationOptions
  ): Promise<void> {
    const pdf = this.createBasePDF();
    const poolInfo = transformFormDataToPoolInfo(options.formData);
    const { surfaceAreaTop, volume: estimateVol } = calculateSurfaceArea({
      info: poolInfo,
    });
    const measures: PDFMeasures = {
      surfaceAreaTop,
      estimateVol: Number(estimateVol.toFixed(2)),
    };

    
    this.addPDFHeader(pdf, "Heat Pump Calculator Results");
    let currentY = 35;

    
    currentY = await this.addCustomerTable(pdf, options, poolInfo, currentY);

    
    currentY = await this.addPoolInfoTable(pdf, poolInfo, measures, currentY);

    
    currentY = await this.addOperatingPeriodTable(pdf, currentY, options);

    
    pdf.setFont("NotoSans-Bold", "normal");
    pdf.setFontSize(14);
    pdf.text(
      "No suitable heat pump recommendations found for your pool configuration.",
      pdf.internal.pageSize.getWidth() / 2,
      currentY + 20,
      { align: "center" }
    );

    
    this.addPDFFooter(pdf);
    getPoolImage(pdf, poolInfo.shape);

    
    await this.handlePDFOutput(pdf, options);
  }

  private async generateFullRecommendationPDF(
    options: PDFGenerationOptions
  ): Promise<void> {
    if (!options.recommendations || !options.graphRef?.current) {
      throw new Error("Recommendations data or reference element is missing");
    }

    const pdf = this.createBasePDF();
    const poolInfo = transformFormDataToPoolInfo(options.formData);

    
    const measures: PDFMeasures = {
      surfaceAreaTop: options.recommendations.poolMeasures.surfaceArea || 0,
      estimateVol: options.recommendations.poolMeasures.volume || 0,
    };

    
    this.addPDFHeader(pdf, "Heat Pump Calculator Results");
    let currentY = 35;

    currentY = await this.addCustomerTable(pdf, options, poolInfo, currentY);
    currentY = await this.addPoolInfoTable(pdf, poolInfo, measures, currentY);
    currentY = await this.addOperatingPeriodTable(pdf, currentY, options);

    this.addPDFFooter(pdf);
    getPoolImage(pdf, poolInfo.shape);
    addDisclaimer(pdf, currentY);

    this.addPDFFooter(pdf);

    
    pdf.addPage();
    this.addPDFHeader(pdf, "Heat Pump Calculator Results");

    
    const standartPump = options.recommendations?.standard.pumpModel;
    const premiumPump = options.recommendations?.premium.pumpModel;

    addPdfRecommendationBanner(
      pdf,
      30,
      options.recommendations.standard.power.toFixed(2),
      options.recommendations.airTemperatureLabel || "27°C"
    );

    
    createRecommendationImages({
      pdf,
      isMultiplePumps: options.recommendations?.isMultiplePumps || false,
      recommendation1: {
        type: "Option 1",
        totalPrice: options.recommendations?.standard.totalPrice,
        annualCOP: options.recommendations?.standard.annualCOP,
        firstHeating: options.recommendations?.standard.firstHeating,
        highestSpeedRate: options.recommendations?.standard.highestSpeedRate,
        pumpModel: standartPump,
      },
      recommendation2: {
        type: "Option 2",
        totalPrice: options.recommendations?.premium.totalPrice,
        annualCOP: options.recommendations?.premium.annualCOP,
        firstHeating: options.recommendations?.premium.firstHeating,
        highestSpeedRate: options.recommendations?.premium.highestSpeedRate,
        pumpModel: premiumPump,
      },
      airTemperatureLabel:
        options.recommendations?.airTemperatureLabel || "27°C",
    });

    const recommendationCanvas = await html2canvas(options.graphRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    });

    const recommendationImgData = recommendationCanvas.toDataURL(
      "image/jpeg",
      3
    );

    
    const isMobile = options.isMobile || false;

    if (isMobile) {
      pdf.addImage(
        recommendationImgData,
        "PNG",
        48, 
        210, 
        100, 
        75, 
        "",
        "NONE"
      );
    } else {
      pdf.addImage(
        recommendationImgData,
        "PNG",
        -30, 
        210, 
        270, 
        70, 
        "",
        "NONE"
      );
    }

    
    
    
    
    this.addPDFFooter(pdf);

    await this.handlePDFOutput(pdf, options);
  }

  private createBasePDF(): jsPDF {
    return new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
  }

  private addPDFHeader(pdf: jsPDF, title: string): void {
    pdf.setFont("NotoSans-Bold", "normal");
    pdf.addImage(DaveyLogo2, "PNG", 80, 10, 35, 6, "", "NONE");
    pdf.setFontSize(12);
    pdf.text(title, 70, 25);
    pdf.setFontSize(8);
    pdf.text(formatDate(new Date()), 15, 32);
  }

  private addPDFFooter(pdf: jsPDF): void {
    pdf.setFontSize(12);
    pdf.setTextColor(0);
    pdf.setFont("NotoSans-Bold", "normal");
    pdf.text(
      "*All results presented are a guide only.",
      pdf.internal.pageSize.getWidth() / 2,
      pdf.internal.pageSize.getHeight() - 10,
      { align: "center" }
    );
  }

  private async addCustomerTable(
    pdf: jsPDF,
    options: PDFGenerationOptions,
    poolInfo: any,
    startY: number
  ): Promise<number> {
    const isNZ =
      poolInfo.country === "New Zealand"
        ? { state: "New Zealand", city: poolInfo.city }
        : { state: poolInfo.state, city: poolInfo.city };

    const customerTable = getCustomerTableData(
      options.user,
      options.email,
      isNZ,
      options.isExistingPool
    );

    let finalY = startY;

    autoTable(pdf, {
      head: [customerTable.header],
      body: customerTable.rows,
      startY: finalY,
      theme: "plain",
      headStyles: {
        fillColor: [255, 210, 0],
        textColor: 0,
        font: "NotoSans-Bold, NotoSans-Regular",
        fontSize: 8,
      },
      bodyStyles: {
        font: "NotoSans-Bold, NotoSans-Regular",
        fontSize: 8,
      },
      didDrawCell: (data) => {
        if (data.section === "body") {
          const { cell, row } = data;
          const { height } = row;
          const cellBottomY = cell.y + height;
          pdf.setDrawColor(225, 226, 235);
          pdf.setLineWidth(0.05);
          pdf.line(cell.x, cellBottomY, cell.x + cell.width, cellBottomY);
        }
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          finalY = data.cursor.y + 10;
        }
      },
    });

    return finalY;
  }

  private async addPoolInfoTable(
    pdf: jsPDF,
    poolInfo: any,
    measures: PDFMeasures | any,
    startY: number
  ): Promise<number> {
    
    if (!measures || typeof measures.estimateVol === "undefined") {
      console.warn(
        "Invalid measures data provided to addPoolInfoTable:",
        measures
      );
      
      measures = {
        surfaceAreaTop: 0,
        estimateVol: 0,
        ...measures,
      };
    }

    const poolInfoTable = getPoolInfoTableData(poolInfo, measures);
    let finalY = startY;

    autoTable(pdf, {
      head: [poolInfoTable.header],
      body: poolInfoTable.rows,
      startY: finalY,
      theme: "plain",
      headStyles: {
        fillColor: [255, 210, 0],
        textColor: 0,
        font: "NotoSans-Bold, NotoSans-Regular",
        fontSize: 8,
      },
      bodyStyles: {
        font: "NotoSans-Bold, NotoSans-Regular",
        fontSize: 8,
      },
      didDrawCell: (data) => {
        if (data.section === "body") {
          const { cell, row } = data;
          const { height } = row;
          const cellBottomY = cell.y + height;
          pdf.setDrawColor(225, 226, 235);
          pdf.setLineWidth(0.05);
          pdf.line(cell.x, cellBottomY, cell.x + cell.width, cellBottomY);
        }
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          finalY = data.cursor.y + 10;
        }
      },
    });

    return finalY;
  }

  private async addOperatingPeriodTable(
    pdf: jsPDF,
    startY: number,
    options: PDFGenerationOptions
  ): Promise<number> {
    if (!options.recommendations) {
      
      pdf.setFont("NotoSans-Bold", "normal");
      pdf.setFontSize(10);
      pdf.text("Operating Period Information", 15, startY + 10);
      return startY + 25;
    }

    
    const monthsStatus: {
      [key: string]: { index: number; name: string; value: boolean };
    } = {};

    const monthNames = [
      "jan",
      "feb",
      "mar",
      "apr",
      "may",
      "jun",
      "jul",
      "aug",
      "sep",
      "oct",
      "nov",
      "dec",
    ];

    options.recommendations.monthsArray.forEach((month, index) => {
      monthsStatus[monthNames[index]] = {
        index: index + 1,
        name: month.name,
        value: month.value,
      };
    });

    
    const activeMonths = options.recommendations.monthsArray.filter(
      (month) => month.value
    );
    const season = activeMonths.length > 6 ? "Year Round" : "Seasonal";

    
    const temperatureData: { [key: number]: number } = {};
    Object.entries(options.recommendations.temperaturePerMonth).forEach(
      ([key, value]) => {
        temperatureData[parseInt(key)] = value;
      }
    );

    const operatingPeriodTable = getOperatingPeriodTableData(
      temperatureData,
      monthsStatus,
      season
    );

    let finalY = startY;

    autoTable(pdf, {
      head: operatingPeriodTable.header,
      body: operatingPeriodTable.rows,
      startY: finalY,
      theme: "plain",
      headStyles: {
        fillColor: [216, 216, 216],
        font: "NotoSansSymbols2-Regular, NotoSans-Bold, NotoSans-Regular",
        fontSize: 8,
      },
      bodyStyles: {
        font: "NotoSansSymbols2-Regular, NotoSans-Bold, NotoSans-Regular",
        fontSize: 8,
      },
      didDrawPage: (data) => {
        if (data.cursor) {
          finalY = data.cursor.y + 10;
        }
      },
    });

    return finalY;
  }

  private async handlePDFOutput(
    pdf: jsPDF,
    options: PDFGenerationOptions
  ): Promise<void> {
    if (options.download) {
      pdf.save(`${options.user} - Davey heat pump recommendation.pdf`);
    } else if (options.latestRecommendationReportId) {
      const pdfDataUri = pdf.output("datauristring");
      const pdfBase64 = pdfDataUri.split(",")[1];

      await salesforceService.sendPDF({
        pdfBase64,
        reportId: options.latestRecommendationReportId,
      });
    }
  }
}


export const pdfGenerator = new PDFGenerator();
